import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, AlertTriangle, TrendingUp, TrendingDown, Check, Edit2, Barcode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatDateOnly } from '@/utils/dateFormat';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { inventoryAPI, warehousesAPI } from '@/services/api';

const PriceSettingsPage = () => {
  const [priceAlerts, setPriceAlerts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState('all');
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [newSellingPrice, setNewSellingPrice] = useState('');
  const [newMRP, setNewMRP] = useState('');
  const [saving, setSaving] = useState(false);
  
  const { toast } = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [alertsRes, warehousesRes] = await Promise.all([
        inventoryAPI.getPriceAlerts({
          warehouse_id: selectedWarehouse !== 'all' ? selectedWarehouse : undefined,
          days: days
        }),
        warehousesAPI.list({ is_active: true })
      ]);
      setPriceAlerts(alertsRes.data || []);
      setWarehouses(warehousesRes.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch price alerts', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [selectedWarehouse, days, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditPrice = (product) => {
    setSelectedProduct(product);
    setNewSellingPrice(product.suggested_selling_price?.toString() || '');
    setNewMRP(product.current_mrp?.toString() || '');
    setEditDialogOpen(true);
  };

  const handleSavePrice = async () => {
    if (!newSellingPrice || parseFloat(newSellingPrice) <= 0) {
      toast({ title: 'Error', description: 'Enter valid selling price', variant: 'destructive' });
      return;
    }
    
    setSaving(true);
    try {
      await inventoryAPI.updateSellingPrice(
        selectedProduct.product_id,
        parseFloat(newSellingPrice),
        newMRP ? parseFloat(newMRP) : null,
        `Price updated due to cost change from GRN ${selectedProduct.grn_number}`
      );
      toast({ title: 'Success', description: 'Selling price updated successfully' });
      setEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to update price', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleApplySuggested = async (product) => {
    try {
      await inventoryAPI.updateSellingPrice(
        product.product_id,
        product.suggested_selling_price,
        null,
        `Auto-applied suggested price from GRN ${product.grn_number}`
      );
      toast({ title: 'Success', description: 'Suggested price applied' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to apply price', variant: 'destructive' });
    }
  };

  // Calculate summary stats
  const increaseCount = priceAlerts.filter(p => p.cost_change_percent > 0).length;
  const decreaseCount = priceAlerts.filter(p => p.cost_change_percent < 0).length;
  const totalCostImpact = priceAlerts.reduce((sum, p) => sum + (p.new_cost_price - p.old_cost_price), 0);

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <Toaster />
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Price Settings</h1>
          <p className="text-gray-500">Review and update selling prices based on GRN cost changes</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="border-gray-300">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Products with Changes</p>
                <p className="text-2xl font-bold text-gray-800">{priceAlerts.length}</p>
              </div>
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Cost Increased</p>
                <p className="text-2xl font-bold text-red-600">{increaseCount}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Cost Decreased</p>
                <p className="text-2xl font-bold text-emerald-600">{decreaseCount}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Net Cost Impact</p>
                <p className={`text-2xl font-bold ${totalCostImpact > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                  {totalCostImpact > 0 ? '+' : ''}₹{totalCostImpact.toFixed(2)}
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Barcode className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-6">
        <Select value={selectedWarehouse} onValueChange={setSelectedWarehouse}>
          <SelectTrigger className="w-48 bg-white border-gray-300">
            <SelectValue placeholder="All Warehouses" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="all">All Warehouses</SelectItem>
            {warehouses.map(wh => (
              <SelectItem key={wh.id} value={wh.id}>{wh.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={days.toString()} onValueChange={(v) => setDays(parseInt(v))}>
          <SelectTrigger className="w-40 bg-white border-gray-300">
            <SelectValue placeholder="Time period" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="14">Last 14 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="60">Last 60 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Price Alerts Table */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : priceAlerts.length === 0 ? (
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="py-12 text-center">
            <Check className="w-12 h-12 text-emerald-400 mx-auto mb-4" />
            <p className="text-gray-500">No price changes detected in the selected period</p>
            <p className="text-gray-400 text-sm">All selling prices are up to date</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardHeader className="pb-4 border-b border-gray-100">
            <CardTitle className="text-lg text-gray-800">
              Products Requiring Price Review
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-amber-50 border-gray-200">
                    <TableHead className="text-gray-700 font-semibold">Product</TableHead>
                    <TableHead className="text-gray-700 font-semibold">Batch / GRN</TableHead>
                    <TableHead className="text-gray-700 font-semibold text-right">Old Cost</TableHead>
                    <TableHead className="text-gray-700 font-semibold text-right">New Cost</TableHead>
                    <TableHead className="text-gray-700 font-semibold text-center">Change %</TableHead>
                    <TableHead className="text-gray-700 font-semibold text-right">Current SP</TableHead>
                    <TableHead className="text-gray-700 font-semibold text-right">Suggested SP</TableHead>
                    <TableHead className="text-gray-700 font-semibold text-center">Margin</TableHead>
                    <TableHead className="text-gray-700 font-semibold text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {priceAlerts.map((alert, index) => (
                    <TableRow key={index} className="border-gray-200 hover:bg-gray-50">
                      <TableCell>
                        <div>
                          <p className="text-gray-800 font-medium">{alert.product_name}</p>
                          <p className="text-teal-600 text-xs">{alert.product_sku}</p>
                          {alert.category_name && (
                            <p className="text-gray-400 text-xs">{alert.category_name}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          {alert.batch_number && (
                            <Badge variant="outline" className="mb-1">{alert.batch_number}</Badge>
                          )}
                          <p className="text-teal-600 text-xs">{alert.grn_number}</p>
                          <p className="text-gray-400 text-xs">
                            {formatDateOnly(alert.grn_date)}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell className="text-right text-gray-600">₹{alert.old_cost_price?.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-medium text-gray-800">₹{alert.new_cost_price?.toFixed(2)}</TableCell>
                      <TableCell className="text-center">
                        <Badge className={`${
                          alert.cost_change_percent > 0 
                            ? 'bg-red-100 text-red-700' 
                            : 'bg-emerald-100 text-emerald-700'
                        }`}>
                          {alert.cost_change_percent > 0 ? '+' : ''}{alert.cost_change_percent?.toFixed(1)}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-gray-600">₹{alert.current_selling_price?.toFixed(2)}</TableCell>
                      <TableCell className="text-right font-medium text-blue-600">₹{alert.suggested_selling_price?.toFixed(2)}</TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="text-gray-600">
                          {alert.current_margin_percent?.toFixed(1)}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 px-2 text-emerald-600 hover:bg-emerald-50"
                            onClick={() => handleApplySuggested(alert)}
                            title="Apply Suggested Price"
                          >
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 px-2 text-blue-600 hover:bg-blue-50"
                            onClick={() => handleEditPrice(alert)}
                            title="Edit Price"
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200 mt-6">
        <CardContent className="pt-4">
          <div className="flex gap-3">
            <Barcode className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-semibold text-blue-800">About Price Settings</p>
              <ul className="text-sm text-blue-700 mt-1 space-y-1">
                <li>• Products are listed when their purchase cost changes in a recent GRN</li>
                <li>• Suggested selling price maintains the same profit margin as before</li>
                <li>• New batch numbers from GRN may require barcode label updates</li>
                <li>• Review and update prices to maintain desired profit margins</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Price Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800">Update Selling Price</DialogTitle>
          </DialogHeader>
          
          {selectedProduct && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="font-semibold text-gray-800">{selectedProduct.product_name}</p>
                <p className="text-teal-600 text-sm">{selectedProduct.product_sku}</p>
                {selectedProduct.batch_number && (
                  <Badge variant="outline" className="mt-2">{selectedProduct.batch_number}</Badge>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="bg-red-50 p-3 rounded">
                  <p className="text-red-600">Old Cost → New Cost</p>
                  <p className="font-bold text-red-700">
                    ₹{selectedProduct.old_cost_price?.toFixed(2)} → ₹{selectedProduct.new_cost_price?.toFixed(2)}
                  </p>
                </div>
                <div className="bg-blue-50 p-3 rounded">
                  <p className="text-blue-600">Suggested SP</p>
                  <p className="font-bold text-blue-700">₹{selectedProduct.suggested_selling_price?.toFixed(2)}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-700">New Selling Price (₹) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newSellingPrice}
                  onChange={(e) => setNewSellingPrice(e.target.value)}
                  className="bg-gray-50 border-gray-300"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-700">MRP (₹)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={newMRP}
                  onChange={(e) => setNewMRP(e.target.value)}
                  placeholder={`Current: ₹${selectedProduct.current_mrp?.toFixed(2)}`}
                  className="bg-gray-50 border-gray-300"
                />
              </div>
              
              {newSellingPrice && selectedProduct.new_cost_price > 0 && (
                <div className="bg-amber-50 p-3 rounded text-sm">
                  <p className="text-amber-700">
                    New Margin: <span className="font-bold">
                      {(((parseFloat(newSellingPrice) - selectedProduct.new_cost_price) / parseFloat(newSellingPrice)) * 100).toFixed(1)}%
                    </span>
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-gray-300">
              Cancel
            </Button>
            <Button onClick={handleSavePrice} disabled={saving} className="bg-teal-500 hover:bg-teal-600 text-white">
              {saving ? 'Saving...' : 'Update Price'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PriceSettingsPage;
